import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, eur, formatApiErrorDetail, RATE_CODES, RATE_LABELS, CATEGORY_TO_BUCKET } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScanLine, Upload, CheckCircle2, Sparkles, Store, CalendarDays, AlertTriangle, ShieldCheck, ArrowLeft, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

const conf = { high: "text-emerald-700 border-emerald-300", medium: "text-amber-600 border-amber-300", low: "text-red-600 border-red-300" };

export default function ZTicket() {
  const [locations, setLocations] = useState([]);
  const [locId, setLocId] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [cats, setCats] = useState({});
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [alreadyBooked, setAlreadyBooked] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)); }, []);

  // warn if a dagontvangst already exists for this vestiging + date
  useEffect(() => {
    if (!proposal || !locId || !date) { setAlreadyBooked(null); return; }
    api.get("/journal-check", { params: { location_id: locId, date } })
      .then((r) => setAlreadyBooked(r.data.exists ? r.data : null))
      .catch(() => setAlreadyBooked(null));
  }, [proposal, locId, date]);

  const onFile = (e) => {
    const f = e.target.files[0];
    setFile(f);
    setProposal(null);
    setZoom(1);
    if (f && f.type.startsWith("image/")) setPreview(URL.createObjectURL(f));
    else setPreview(null);
  };

  const runOcr = async () => {
    if (!file) return toast.error("Kies eerst een Z-ticket");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (locId) fd.append("location_id", locId);
      const { data } = await api.post("/ocr/zticket", fd);
      setProposal(data);
      setCats({ ...data.categories });
      setDate(data.date || new Date().toISOString().slice(0, 10));
      if (data.preview_data_url) setPreview(data.preview_data_url);
      if (data.matched_location_id) setLocId(data.matched_location_id);
      if (data.matched_from_filename) toast.success(`Winkel automatisch herkend: ${data.matched_location_name}`);
      else toast.success("Z-ticket uitgelezen — controleer het voorstel");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // derive dagboek buckets (0/6/21/E) from category amounts
  const gross_by_rate = RATE_CODES.reduce((acc, c) => ({ ...acc, [c]: 0 }), {});
  Object.entries(cats).forEach(([cat, amt]) => {
    const bucket = CATEGORY_TO_BUCKET[cat] || "21";
    gross_by_rate[bucket] = Math.round((gross_by_rate[bucket] + (parseFloat(amt) || 0)) * 100) / 100;
  });
  const catSum = Math.round(Object.values(cats).reduce((s, v) => s + (parseFloat(v) || 0), 0) * 100) / 100;
  const zTotal = proposal?.z_total || 0;
  const reconciled = zTotal ? Math.abs(catSum - zTotal) < 0.05 : true;

  const confirm = async () => {
    if (!locId) return toast.error("Kies een vestiging");
    setSaving(true);
    try {
      const gbr = {};
      RATE_CODES.forEach((c) => (gbr[c] = gross_by_rate[c] || 0));
      await api.post("/journal", {
        location_id: locId, date, entry_type: "receipts", gross_by_rate: gbr,
        categories: cats, note: `Geboekt via Z-ticket OCR${proposal.z_number ? " (Z" + proposal.z_number + ")" : ""}`,
        source: "z-ticket", z_document_id: proposal.z_document_id,
      });
      toast.success("Dagontvangst definitief geboekt op basis van Z-ticket");
      setProposal(null); setFile(null); setPreview(null); setCats({});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="zticket-page">
      <div>
        <Link to="/journal" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3" data-testid="back-to-journal">
          <ArrowLeft className="h-4 w-4" /> Terug naar Dagboek
        </Link>
        <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
          <ScanLine className="h-7 w-7 text-primary" /> Z-ticket OCR
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Upload het Z-totaal. Noem het bestand naar de winkel (Brustem / Sint-Truiden / Leesberg) + datum,
          dan koppelt het programma zelf de juiste vestiging en doet een voorstel.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="shadow-sm lg:col-span-2">
          <CardHeader><CardTitle className="font-heading text-lg">1. Upload Z-ticket</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>Vestiging (leeg = automatisch uit bestandsnaam)</Label>
              <Select value={locId} onValueChange={setLocId}>
                <SelectTrigger data-testid="zticket-location"><SelectValue placeholder="Automatisch herkennen" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors">
              <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="zticket-file" onChange={onFile} />
              {!preview && file ? (
                <div className="text-foreground py-6"><Upload className="h-8 w-8 mx-auto mb-2 text-primary" /><div className="text-sm font-medium">{file.name}</div></div>
              ) : !preview ? (
                <div className="text-muted-foreground py-6">
                  <Upload className="h-8 w-8 mx-auto mb-2" />
                  <div className="text-sm">Klik om een foto, scan of PDF van het Z-ticket te kiezen</div>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">Klik om een ander Z-ticket te kiezen</div>
              )}
            </label>

            {preview && (
              <div className="space-y-2" data-testid="zticket-preview">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Voorbeeld (controleer het ticket)</Label>
                  <div className="flex items-center gap-1">
                    <Button type="button" variant="outline" size="icon" className="h-7 w-7" data-testid="zoom-out"
                            onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))}>
                      <ZoomOut className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs tabular-nums w-10 text-center">{Math.round(zoom * 100)}%</span>
                    <Button type="button" variant="outline" size="icon" className="h-7 w-7" data-testid="zoom-in"
                            onClick={() => setZoom((z) => Math.min(4, Math.round((z + 0.25) * 100) / 100))}>
                      <ZoomIn className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" data-testid="zoom-reset"
                            onClick={() => setZoom(1)} title="Herstellen">
                      <Maximize2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="max-h-[520px] overflow-auto rounded-md border border-border bg-muted/30 p-2">
                  <img src={preview} alt="Z-ticket" style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}
                       className="mx-auto rounded transition-transform duration-150" />
                </div>
              </div>
            )}
            <Button onClick={runOcr} disabled={loading} className="w-full" data-testid="run-ocr">
              <Sparkles className="h-4 w-4 mr-2" />{loading ? "AI leest ticket…" : "Uitlezen met AI"}
            </Button>
          </CardContent>
        </Card>

        <Card className="shadow-sm lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">2. Controleer voorstel</CardTitle>
            {proposal && <Badge variant="outline" className={conf[proposal.confidence] || ""}>Betrouwbaarheid: {proposal.confidence}</Badge>}
          </CardHeader>
          <CardContent className="space-y-4">
            {!proposal ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Nog geen voorstel. Upload en lees eerst een Z-ticket uit.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                    <Store className="h-4 w-4 text-primary" />
                    {proposal.matched_location_name || "— kies vestiging —"}
                    {proposal.matched_from_filename && <Badge className="ml-auto bg-emerald-100 text-emerald-700 hover:bg-emerald-100">auto</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    <Input type="date" data-testid="zticket-date" value={date} onChange={(e) => setDate(e.target.value)} />
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Categorie-overzicht ({proposal.category_scheme === "leesberg_5" ? "De Leesberg — 5 categorieën" : "Singh Express — 4 categorieën"})</Label>
                  <Table className="mt-1.5">
                    <TableHeader>
                      <TableRow><TableHead>Categorie</TableHead><TableHead className="text-right">Bedrag incl.</TableHead><TableHead>Dagboek</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.keys(cats).map((cat) => (
                        <TableRow key={cat}>
                          <TableCell className="font-medium">{proposal.category_labels[cat] || cat}</TableCell>
                          <TableCell className="text-right">
                            <Input type="number" step="0.01" className="w-32 ml-auto text-right tabular-nums h-8"
                                   data-testid={`cat-${cat}`} value={cats[cat]}
                                   onChange={(e) => setCats({ ...cats, [cat]: e.target.value })} />
                          </TableCell>
                          <TableCell><Badge variant="outline">{RATE_LABELS[CATEGORY_TO_BUCKET[cat]]}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div>
                  <Label className="text-sm">Dagboek-split (incl. BTW)</Label>
                  <div className="grid grid-cols-4 gap-2 mt-1.5">
                    {RATE_CODES.map((c) => (
                      <div key={c} className="rounded-md border border-border px-3 py-2" data-testid={`zt-bucket-${c}`}>
                        <div className="text-xs text-muted-foreground">{RATE_LABELS[c]}</div>
                        <div className="font-heading tabular-nums text-sm font-semibold">{eur(gross_by_rate[c])}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {alreadyBooked && (
                  <div className="flex items-start gap-2 rounded-md px-3 py-2 text-sm bg-red-50 text-red-700 border border-red-200" data-testid="zt-already-booked">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>Kijk zeker goed na — er is voor deze vestiging op <b>{date}</b> al een dagontvangst geboekt ({eur(alreadyBooked.total_gross)}). Een tweede boeking op dezelfde dag wordt geweigerd; gebruik zo nodig een correctie.</span>
                  </div>
                )}
                <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${reconciled ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="zt-reconcile">
                  {reconciled ? <ShieldCheck className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                  Z-totaal op bon: <b>{eur(zTotal)}</b> · Som categorieën: <b>{eur(catSum)}</b>
                  {reconciled ? " — sluit aan ✓" : " — WIJKT AF, controleer de bon"}
                </div>
                {proposal.notes && <p className="text-xs text-muted-foreground italic">AI-notitie: {proposal.notes}</p>}

                <Button onClick={confirm} disabled={saving || !locId} className="w-full" data-testid="confirm-zticket">
                  <CheckCircle2 className="h-4 w-4 mr-2" />{saving ? "Boeken…" : "Bevestigen en definitief boeken"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
